import { useMemoQueriesResults } from '@chronosphereio/shared';
import { useQueries } from '@tanstack/react-query';
import { EventDefinition } from '../../../core';
import { QueryPluginContext } from '../../model';
import { getQueryOptions, usePluginQueryContext } from '../common';
import { processPluginDefinition } from '../data-querying';
import { usePluginRegistry, usePlugins } from '../plugin-registry';
export const ANNOTATION_QUERY_KEY = 'EventQuery';

export interface UseEventQueryOptions {
  suggestedStepMs?: number;
}

/**
 * Runs multiple event queries using plugins and returns the results.
 */
export function useEventQueries(definitions: EventDefinition[], options?: UseEventQueryOptions) {
  const { getPlugin } = usePluginRegistry();
  const pluginLoaderResponse = usePlugins(
    ANNOTATION_QUERY_KEY,
    definitions.map((d) => ({ kind: d.spec.plugin.kind }))
  );
  const context = usePluginQueryContext();

  return useMemoQueriesResults(
    useQueries({
      queries: definitions.map((definition, idx) => {
        const plugin = pluginLoaderResponse[idx]?.data;
        const { queryEnabled, queryKey } = getQueryOptions({ plugin, definition, context });
        const eventEnabled = definition.spec.enabled !== false;
        const ctx: QueryPluginContext = {
          ...context,
          suggestedStepMs: options?.suggestedStepMs,
        };

        return {
          enabled: eventEnabled && queryEnabled,
          queryKey: queryKey,
          queryFn: async () => {
            const plugin = await getPlugin(ANNOTATION_QUERY_KEY, definition.spec.plugin.kind);
            const { definition: modifiedDefinition } = await processPluginDefinition({
              context: ctx,
              definition: definition.spec.plugin,
              plugin: plugin,
            });
            const data = await plugin.getEvents(modifiedDefinition.spec, ctx);
            return data;
          },
          staleTime: Infinity,
          cacheTime: 0,
        };
      }),
    })
  );
}

/**
 * Runs a single event query using plugins and returns the results.
 */
export function useEventQuery(definition: EventDefinition, options?: UseEventQueryOptions) {
  const results = useEventQueries([definition], options);
  return results[0];
}
